"use strict";
import Questions from "./Questions";
import schemaElevator from "./_schemaElevator";
import knownQuestionDefaults from "./_schemaElevator_knownQuestionDefaults";
import sortKeyUtilsGen from "./_sortKeyUtils";
const sortKeyUtils = sortKeyUtilsGen(); /*standard base 64 sortKeys*/
import Choices from "./Choices";
import QuestionType from "./QuestionType";
import QuestionDependency from "./QuestionDependency";
import Answer from "./Answer";
const _keys = require("lodash/keys");
const _values = require("lodash/values");

const allTrue = (list, func) => list.reduce((acc, val) => acc && func(val), true);

class Question {

    private _questions: Questions;
    private _questionId: string;

    constructor(questions, questionId) {
        this._questions = questions;
        this._questionId = questionId;
    }

    get id() {
        return this._questionId;
    }

    get _q() {
        return this._questions._qGetRawCustomQuestion(this._questionId);
    }

    get _qDefault() {
        return schemaElevator.elevateQuestion(this.id) || {};
    }

    get _o() {
        return this._questions._qGetRawOverride(this._questionId);
    }

    get _s() {
        return this._questions._qGetRawSortKey(this._questionId);
    }

    get _ds() {
        return this._questions._ds;
    }

    _updateProperty(propertyName, newPropertyValue, callback) {
        const defValue = this._qDefault[propertyName];

        /**
         *
         * There are 3 cases here.
         * 1. Write newValue
         * 2. Write null
         * 3. Keep what is in the database - we will still write (null/newValue) in this case.
         *
         * So there are really just 2 outcomes
         * 1. Write newValue
         * 2. Write null
         */
        let newValue = newPropertyValue;
        if (defValue == newValue) {
            newValue = null;
        }

        return this._questions._qWriteRawCustomQuestionProperty(this.id, propertyName, newValue, callback);
    }

    get title() {
        return this._q.title;
    }

    updateTitle(newQuestionText, callback) {
        return this._updateProperty("title", newQuestionText, callback);
    }

    get questionText() {
        return this._q.question;
    }

    updateQuestionText(newQuestionText, callback) {
        return this._updateProperty("question", newQuestionText, callback);
    }

    get isEnabled() {
        return ((typeof this._o != "undefined" && this._o !== null ) ? this._o : true) && this.allDependenciesEnabled;
    }

    updateEnabled(newIsEnabled, callback) {
        const isEnabled = schemaElevator.unelevateOverride(this.id, newIsEnabled);
        return this._questions._qWriteRawOverride(this.id, isEnabled, callback);
    }

    get isAskedToPlusOnes() {
        return this._q.askPlusOnes !== false;
    }

    updateAskedToPlusOnes(askPlusOnes, callback) {
        return this._updateProperty("askPlusOnes", askPlusOnes ? true : false, callback);
    }

    get allDependenciesEnabled() {
        return allTrue(_values(this.dependencies), d => d.question.isEnabled);
    }

    get questions() {
        return this._questions;
    }

    get type() {
        return new QuestionType(this._q.type);
    }

    get sortKey() {
        return this._s;
    }

    updateSortKey(newSortKey, callback) {
        const sortKey = schemaElevator.unelevateSortKey(this.id, newSortKey);
        this._questions._qWriteRawSortKey(this.id, sortKey, callback);
    }

    get dependencies() {
        return (_values(this._q.dependencies) || [])
          .map(d => new QuestionDependency(this, new Question(this._questions, d.questionId), d.answer));
    }

    addDependency(questionId, answer) {
        const dependency: any = { questionId };
        if(answer) dependency.answer = answer;
        return this._questions._qCreateRawCustomQuestionDependency(this._questionId, dependency);
    }

    removeDependency(questionId, answer) {
        const depIds = _keys(this._q.dependencies).filter(dId => {
          const d = this._q.dependencies[dId];
          return d.questionId === questionId && (answer === undefined || answer === d.answer);
        });
        return Promise.all(depIds.map(dId => this._questions._qWriteRawCustomQuestionDependency(this._questionId, dId, null)));
    }

    dependenciesAnswered(person) {
        return allTrue(_values(this.dependencies), d => d.questionIsAnsweredBy(person));
    }

    dependenciesFulfilled(person) {
        return allTrue(_values(this.dependencies), d => d.isMetBy(person));
    }

    _sortKeyBetween(keyLess, keyMore) {
        if(!keyLess) {
            keyLess = sortKeyUtils.minChar + (keyMore ? keyMore : "");
        }

        if (!keyMore) {
            keyMore = sortKeyUtils.maxChar + (keyLess ? keyLess : "");
        }

        return sortKeyUtils.mid(keyLess, keyMore);
    }

    updateSortKeyBetween(keyLess, keyMore, callback) {
        return this.updateSortKey(this._sortKeyBetween(keyLess, keyMore), callback);
    }

    /**
     * Gets you the hypothetical sortkey which will be after this question
     * but before the next one
     */
    get sortKeyHigher() {
        const lowKey = this.sortKey;
        const nextQ = this._questions.questionAtIndex(this._questions.indexOfQuestion(this) + 1);
        const highKey = nextQ ? nextQ.sortKey : null;
        return this._sortKeyBetween(lowKey, highKey);
    }

    /**
     * Gets you the hypothetical sortkey which will be after this question
     * but before the previous one
     */
    get sortKeyLower() {
        const highKey = this.sortKey;
        const nextQ = this._questions.questionAtIndex(this._questions.indexOfQuestion(this) - 1);
        const lowKey = nextQ ? nextQ.sortKey : null;
        return this._sortKeyBetween(lowKey, highKey);
    }

    get answerKey() {
        return (this._q.prefix || "") + this.id;
    }

    get choices() {
        if (!this.type.isMultipleChoice()) {
            return null;
        }

        return new Choices(this._questions, this.id);
    }

    compare(q) {
        var s1 = this.sortKey;
        var s2 = q.sortKey;
        return (s1 == s2) ? 0 : ((s1 < s2) ? -1 : +1);
    }

    answerObjToJson(answerObj) {
        if (this.type.isMultipleChoice()) {
            return answerObj.storeValue;
        }
        else if (this.type.isPlaintext()) {
            return answerObj;
        }
        else {
            throw new Error("Unknown Question Format");
        }
    }

    answerJsonToObj(answerJson) {
        if (this.type.isMultipleChoice()) {
            return this.choices.choiceFromJson(answerJson);
        }
        else if (this.type.isPlaintext()) {
            return answerJson || null;
        }
        else {
            throw new Error("Unknown Question Format");
        }
    }

    equals(q) {
        return q && typeof q === "object" && q.id && q.id === this.id;
    }

    answerForPerson (person) {
        return new Answer(this, person);
    }

    isAnsweredBy(person) {
        return this.answerForPerson(person).isAnswered;
    }

    followUpsFor(person) {
        return this.questions.dependentOnQuestion(this)
          .filter(q => allTrue(_values(q.dependencies), d => d.isMetBy(person)));
    }

    createNewNextQuestion(type, data) {
        data.type = type;
        return this._questions._createNewQuestionWithSortKey(data, this.sortKeyHigher);
    }

    createNewPreviousQuestion(type, data) {
        data.type = type;
        return this._questions._createNewQuestionWithSortKey(data, this.sortKeyLower);
    }

    toString() {
        return [
            `${this.id}: ${this.title}`,
            `${this.questionText}`,
            `type: ${this.type}`,
            `enabled: ${this.isEnabled}`,
            `answerKey: ${this.answerKey}`,
            `choices: ${this.type.isMultipleChoice() ? this.choices.toString() : "n/a"}`
        ].join("\n\t");
    }

    get isAJoyQuestion() {
        const joyKnownQuestionKeys = Object.keys(knownQuestionDefaults.customQuestions);
        return joyKnownQuestionKeys.indexOf(this.id) !== -1;
    }

    shouldAskForPerson(person) {
      return (
        this.isEnabled &&
        this.shouldCountForPerson(person)
      );
    }

    shouldCountForPerson(person) {
      return (
        (person.isHeadOfHousehold || this.isAskedToPlusOnes) &&
        (!this.dependenciesAnswered(person) || this.dependenciesFulfilled(person))
      );
    }
}

export default Question;
